/* eslint-disable @typescript-eslint/no-explicit-any */
import { CircularProgress, Divider } from '@mui/material'
import { getTagsInfo } from 'api/get/reach/reach'
import clsx from 'clsx'
import { Button, Modal, TagAudience, TitleBox } from 'components'
import { ListTags } from 'components/confirmationModal/listTags'
import { TitleContent } from 'components/confirmationModal/titleContent'
import { TitleContentRecurrence } from 'components/confirmationModal/titleContentRecurrence'
import { useSearchDebounce } from 'hooks'
import { ContentResponse, Icons } from 'interfaces'
import React, { useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { formatLocalTime } from 'utils'

interface CalendarPanelModalProps {
	viewMore: boolean
	setViewMore: (value: boolean) => void
	data: ContentResponse
	dataLocation: any
	dataAudience: any
	isLoading: boolean
	isLoadingLocation: boolean
	isLoadingAudience: boolean
	organizationId: number
	hasVideo: boolean
	deliveryEnhancements: any
	hasAudience: boolean
	hasTags: boolean
	hasStaticLists: boolean
	hasDynamicLists: boolean
	hasIndividuals: boolean
	id: number
	handleCopy: (url: string) => void
}

const formatCategory = (category?: string): string => {
	if (!category) return ''

	return category
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

export const CalendarPanelModal: React.FC<CalendarPanelModalProps> = ({
	viewMore,
	setViewMore,
	data,
	dataLocation,
	dataAudience,
	isLoading,
	isLoadingLocation,
	isLoadingAudience,
	organizationId,
	hasVideo,
	deliveryEnhancements,
	hasAudience,
	hasTags,
	hasStaticLists,
	hasDynamicLists,
	id,
	hasIndividuals,
	handleCopy
}) => {
	const numberElement = 10
	const [isOpenTagBoxModal, setIsOpenTagBoxModal] = useState(false)
	const [page, setPage] = useState<number>(1)
	const [valueItemsTags, setValueItemsTags] = useState(numberElement)
	const [searchTags, setSearchTags] = useState('')
	const navigate = useNavigate()

	const {
		inputSearch: inputSearchTags,
		handleChangeDebounce: handleChangeDebounceTags,
		resetInput: resetInputTags
	} = useSearchDebounce('')

	const {
		data: dataTagBox,
		isLoading: isLoadingTagBox,
		hasNextPage: hasNextPageTagsReach,
		fetchNextPage: fetchNextPageTagsReach,
		isFetchingNextPage: isFetchingNextPageTagsReach
	} = useInfiniteQuery({
		queryKey: [
			'GET_TAGS_BOX',
			data?.delivery_enhancements?.audience?.tags,
			organizationId,
			page,
			valueItemsTags,
			inputSearchTags
		],
		queryFn: ({ pageParam = 1 }) =>
			getTagsInfo(
				data?.delivery_enhancements?.audience?.tags as number[],
				organizationId,
				pageParam,
				valueItemsTags,
				inputSearchTags
			),
		getNextPageParam: response => {
			const nextPage = Number(response?.page) + 1
			if (nextPage <= Number(response?.pages)) {
				return nextPage
			}
			return undefined
		},
		enabled:
			data &&
			data.delivery_enhancements?.audience?.tags &&
			data.delivery_enhancements.audience.tags.length > 0 &&
			isOpenTagBoxModal,
		refetchOnWindowFocus: false
	})

	const hasDeliveryEnhancements =
		deliveryEnhancements?.schedule?.timing.expiration_date ||
		data?.delivery_enhancements?.schedule?.custom_recurrence
			.repeat_start_date ||
		deliveryEnhancements?.push_notification?.custom_recurrence
			?.repeating_schedule ||
		deliveryEnhancements?.good_link?.link_call_action ||
		data?.delivery_enhancements?.locations?.[0]?.address ||
		data?.delivery_enhancements?.locations?.[0]?.location_id

	return (
		<Modal
			marginL
			closeButton
			type="normal"
			maxWidth="max-w-[70vw]"
			open={viewMore}
			title="Preview"
			setOpen={setViewMore}
			icon={Icons.preview}
			className="z-[2001]"
		>
			<>
				<div className="overflow-y-auto max-h-[70vh]">
					{isLoading || isLoadingLocation || isLoadingAudience ? (
						<div className="flex h-full w-full justify-center items-center">
							<CircularProgress disableShrink />
						</div>
					) : (
						<>
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex items-center justify-between gap-4">
									<TitleContent
										title="Type"
										content={formatCategory(
											data?.basic_setup.category as string
										)}
										classNamesButton="px-4 py-0"
									/>
									<TitleContent
										title="Sharing"
										content={data?.basic_setup.sharing ? 'On' : 'Off'}
										classNamesButton="px-4 py-0"
									/>
									<TitleContent
										title="Saving"
										content={data?.basic_setup.saving ? 'On' : 'Off'}
										classNamesButton="px-4 py-0"
									/>
									<TitleContent
										title="Push"
										content={
											deliveryEnhancements?.push_notification &&
											Object.keys(deliveryEnhancements.push_notification)
												.length > 0
												? 'On'
												: 'Off'
										}
										noBorder
										classNamesButton="px-4 py-0"
									/>
								</div>
							</div>

							{/* Content Details */}
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex items-stretch gap-4">
									<div className="border border-blue-primary rounded-lg p-4 flex-grow flex flex-col h-full min-h-[197px] min-w-[200px]">
										<div className="flex-grow">
											<p className="text-[15px] text-black-1 font-bold font-Inter">
												{data?.set_up.title}
											</p>
											<hr className="my-2" />
											<div
												className="text-[15px] text-black-1 font-normal font-Inter"
												// eslint-disable-next-line react/no-danger
												dangerouslySetInnerHTML={{
													__html: data?.set_up.description || ''
												}}
											/>
										</div>
									</div>
								</div>
							</div>

							{/* Assets */}
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex items-center justify-between gap-4">
									<TitleContent
										title="Documents"
										content={
											data?.delivery_enhancements?.documents?.length.toString() ||
											'0'
										}
										classNamesButton="px-4 py-0"
									/>
									<TitleContent
										title="Images"
										content={
											data?.basic_setup.preview?.files?.length.toString() || '0'
										}
										classNamesButton="px-4 py-0"
									/>
									<TitleContent
										title="Videos"
										content={hasVideo ? '1' : '0'}
										classNamesButton="px-4 py-0"
										noBorder
									/>
								</div>
							</div>

							{/* Delivery Enhancements */}
							{hasDeliveryEnhancements && (
								<div className="border border-blue-primary rounded-lg p-4 my-4">
									{(deliveryEnhancements?.schedule?.timing.expiration_date ||
										data?.delivery_enhancements?.schedule?.custom_recurrence
											.repeat_start_date) && (
										<>
											<p className="text-[15px] text-gray-18 font-bold font-Inter">
												Content Recurrence
											</p>

											<TitleContentRecurrence
												viewTitle={false}
												hasDivider={false}
												type="content"
												endTitle={
													deliveryEnhancements?.schedule?.custom_recurrence.ends
														.repeat_end_type === 'after_ocurrence'
														? 'after'
														: 'on'
												}
												publish={formatLocalTime(
													deliveryEnhancements?.schedule?.timing
														.release_date as string,
													'MM/DD/YYYY'
												)}
												expire={formatLocalTime(
													deliveryEnhancements?.schedule?.timing
														.expiration_date as string,
													'MM/DD/YYYY'
												)}
												recurrent={
													!deliveryEnhancements?.schedule?.timing.release_date
												}
												endsRecurrent={
													deliveryEnhancements?.schedule?.custom_recurrence.ends
														.repeat_end_type === 'never'
														? 'Never'
														: deliveryEnhancements?.schedule?.custom_recurrence
																.ends.repeat_end_type === 'after_ocurrence'
														? `${deliveryEnhancements?.schedule?.custom_recurrence.ends.repeat_end_value} Occurrences`
														: formatLocalTime(
																deliveryEnhancements?.schedule
																	?.custom_recurrence.ends
																	.repeat_end_date as string,
																'MM/DD/YYYY'
														  )
												}
												publishRecurrent={formatLocalTime(
													deliveryEnhancements?.schedule?.custom_recurrence
														.repeat_start_date as string,
													'MM/DD/YYYY'
												)}
												// eslint-disable-next-line max-len
												repeatEveryRecurrent={`${deliveryEnhancements?.schedule?.custom_recurrence.repeat_every_value} ${deliveryEnhancements?.schedule?.custom_recurrence.repeat_every_type}`}
												repeatOnRecurrent={formatLocalTime(
													deliveryEnhancements?.schedule?.custom_recurrence
														.repeat_on as string,
													'MM/DD/YYYY'
												)}
												listDaysRecurrence={
													deliveryEnhancements?.schedule?.custom_recurrence
														.repeat_every_type === 'week'
														? deliveryEnhancements.schedule?.custom_recurrence.repeat_on
																// eslint-disable-next-line no-useless-escape
																.replace(/[\[\]']+/g, '')
																.split(',')
														: undefined
												}
											/>
										</>
									)}
									{deliveryEnhancements?.push_notification?.custom_recurrence
										?.repeating_schedule && (
										<>
											<p className="text-[15px] text-gray-18 font-bold font-Inter">
												Push Recurrence
											</p>
											<TitleContentRecurrence
												viewTitle={false}
												hasDivider={false}
												type="push"
												endTitle={
													deliveryEnhancements?.push_notification
														?.custom_recurrence?.ends?.repeat_end_type ===
													'after_ocurrence'
														? 'after'
														: 'on'
												}
												publish={formatLocalTime(
													deliveryEnhancements?.push_notification?.timing
														?.release_date as string,
													'MM/DD/YYYY'
												)}
												recurrent={
													deliveryEnhancements?.push_notification?.timing
														?.release_date === null ||
													deliveryEnhancements?.push_notification?.timing
														?.release_date === ''
												}
												endsRecurrent={
													deliveryEnhancements?.push_notification
														?.custom_recurrence?.ends?.repeat_end_type ===
													'never'
														? 'Never'
														: deliveryEnhancements?.push_notification
																?.custom_recurrence?.ends?.repeat_end_type ===
														  'after_ocurrence'
														? `${deliveryEnhancements.push_notification.custom_recurrence.ends.after_ocurrences} Occurrences`
														: formatLocalTime(
																deliveryEnhancements?.push_notification
																	?.custom_recurrence?.ends
																	?.repeat_end_date as string,
																'MM/DD/YYYY'
														  )
												}
												publishRecurrent={formatLocalTime(
													deliveryEnhancements.push_notification
														.custom_recurrence.repeat_start_date as string,
													'MM/DD/YYYY'
												)}
												// eslint-disable-next-line max-len
												repeatEveryRecurrent={`${deliveryEnhancements.push_notification.custom_recurrence.repeat_every_value} ${deliveryEnhancements.push_notification.custom_recurrence.repeat_every_type}`}
												repeatOnRecurrent={formatLocalTime(
													deliveryEnhancements.push_notification
														.custom_recurrence.repeat_on as string,
													'MM/DD/YYYY'
												)}
												listDaysRecurrence={
													deliveryEnhancements.push_notification
														.custom_recurrence.repeat_every_type === 'week'
														? deliveryEnhancements.push_notification.custom_recurrence.repeat_on
																// eslint-disable-next-line no-useless-escape
																.replace(/[\[\]']+/g, '')
																.split(',')
														: undefined
												}
											/>
										</>
									)}

									<div>
										{deliveryEnhancements?.good_link?.link_call_action && (
											<div className="flex items-center justify-between gap-2">
												<TitleContent
													isFull
													title="Goodlink"
													content={
														deliveryEnhancements?.good_link
															?.link_call_action as string
													}
												>
													<Button
														onClick={() =>
															handleCopy(
																deliveryEnhancements?.good_link
																	?.link_call_action as string
															)
														}
														iconLeft={Icons.contentCopy}
														color="Primary"
														className="!min-w-[unset] !border-none"
													/>
												</TitleContent>

												{deliveryEnhancements?.good_link
													?.sublink_call_action !== '' && (
													<TitleContent
														isFull
														title="Sublink"
														content={
															deliveryEnhancements?.good_link
																?.sublink_call_action as string
														}
														noBorder
													>
														<Button
															onClick={() =>
																handleCopy(
																	deliveryEnhancements?.good_link
																		?.sublink_call_action as string
																)
															}
															iconLeft={Icons.contentCopy}
															iconClassName="w-[24px] h-[24px]"
															color="Primary"
															className="!min-w-[unset] !border-none"
														/>
													</TitleContent>
												)}
											</div>
										)}
										{data?.delivery_enhancements?.locations?.[0]?.location_id &&
											dataLocation &&
											dataLocation.coordinates && (
												<div className="flex items-center justify-between gap-2">
													<TitleContent
														isFull
														title="Location"
														content={dataLocation.coordinates.address as string}
													>
														<Button
															onClick={() =>
																handleCopy(
																	dataLocation.coordinates.address as string
																)
															}
															iconLeft={Icons.contentCopy}
															color="Primary"
															className="!min-w-[unset] !border-none "
														/>
													</TitleContent>

													<div className="w-full justify-between" />
												</div>
											)}
										{data?.delivery_enhancements?.locations?.[0]?.address && (
											<div className="flex items-center gap-2">
												<TitleContent
													title="Location"
													content={
														data?.delivery_enhancements?.locations?.[0]?.address
													}
												>
													<Button
														onClick={() =>
															handleCopy(
																data?.delivery_enhancements?.locations?.[0]
																	?.address as string
															)
														}
														iconLeft={Icons.contentCopy}
														color="Primary"
														className="!min-w-[unset] !border-none"
													/>
												</TitleContent>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Audience Section */}
							<div className="border border-blue-primary rounded-lg p-4 my-4">
								<div className="flex flex-row justify-between items-center gap-2">
									<TitleBox icon={Icons.audience} title="Audience:" />
									<p
										className={clsx(
											`text-[22px] text-white font-normal font-Inter border border-blue-primary bg-blue-primary rounded-full px-2 ${
												dataAudience?.reach &&
												dataAudience?.reach.toString().length === 1
													? 'w-[44px] h-[44px] flex items-center justify-center'
													: 'max-w-max'
											} capitalize mb-4`
										)}
									>
										{dataAudience?.reach || 0}
									</p>
								</div>

								<div className="overflow-y-auto max-h-[300px]">
									{hasAudience && (
										<>
											{hasTags && (
												<>
													<TitleContent
														title="Tags:"
														content={
															dataAudience?.detailed_reach?.tag.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
														onClickContent={() => {
															setIsOpenTagBoxModal(true)
														}}
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.tag.list || []
														}
													/>
													<Divider />
												</>
											)}
											{hasStaticLists && (
												<>
													<TitleContent
														title="Static Lists:"
														content={
															dataAudience?.detailed_reach?.static_list.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.static_list.list ||
															[]
														}
													/>
													<Divider />
												</>
											)}
											{hasDynamicLists && (
												<>
													<TitleContent
														title="Dynamic Lists:"
														content={
															dataAudience?.detailed_reach?.dynamic_list.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.dynamic_list.list ||
															[]
														}
													/>
													<Divider />
												</>
											)}
											{hasIndividuals && (
												<>
													<TitleContent
														title="Individuals:"
														content={
															dataAudience?.detailed_reach?.individual.total.toString() ||
															'0'
														}
														noBorder
														isFull
														isBig
													/>
													<ListTags
														listTags={
															dataAudience?.detailed_reach?.individual.list ||
															[]
														}
													/>
												</>
											)}
										</>
									)}

									{!hasAudience && !isLoading && (
										<TitleContent
											title=" All of My People:"
											content={dataAudience?.reach?.toString() ?? '0'}
											noBorder
											isFull
											isBig
										/>
									)}
								</div>
							</div>
							<div className="flex items-center justify-end mt-4 gap-6">
								<Button
									type="button"
									label="Edit"
									color="Primary"
									iconRight={Icons.edit}
									className="!min-w-[130px]"
									onClick={() => navigate(`/content/edit/${id}`)}
								/>
							</div>
						</>
					)}
				</div>

				{/* Tags Box Modal */}
				{isOpenTagBoxModal && (
					<Modal
						setOpen={setIsOpenTagBoxModal}
						open={isOpenTagBoxModal}
						type="normal"
						sizeModal="h-[418px] p-2"
						maxWidth="max-w-[302px]"
						marginL
					>
						<TagAudience
							resetInput={resetInputTags}
							handleChangeDebounce={handleChangeDebounceTags}
							setSearchTags={setSearchTags}
							searchTags={searchTags}
							dataTagsAudience={dataTagBox}
							isLoadingTagsAudience={isLoadingTagBox}
							page={page}
							setPage={setPage}
							valueItems={valueItemsTags}
							setValueItems={setValueItemsTags}
							fetchNextPageTagsReach={fetchNextPageTagsReach}
							hasNextPageTagsReach={hasNextPageTagsReach}
							isFetchingNextPageTagsReach={isFetchingNextPageTagsReach}
						/>
					</Modal>
				)}
			</>
		</Modal>
	)
}
